import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import tw from 'tailwind.macro';
import ButtonSubmit from '../../../components/ButtonSubmit';
import { DestinationSection } from '../../../components/DestinationSection';
import { faqsPropTypes } from '../../../components/FAQSection/constants';
import H1 from '../../../components/H1';
import H2 from '../../../components/H2';
import LazyLoading from '../../../components/LazyLoading';
import {
  CustomH2,
  CustomH3,
  HTMLText,
  SectionCol,
} from '../../../components/SEO';
import { generalTablePropTypes } from '../../../components/Tables/TableHorizontal';
import { formatRoundedDuration, getRoundPrice } from '../../../helpers/results';
import { FileContext } from '../../../services/files/types';
import Accordion from '../../HomePage/View/FAQ/Accordion';
import { EditableSearchContent } from '../../SearchHeader/EditableSearchHeader';
import {
  cityPropTypes,
  DESTINATIONS,
  enhancedDestinationCard,
  FAQ,
  linksPropTypes,
  SEARCH_PAGE_HEADER_INITIAL_MESSAGE_ID,
  SEARCH_PAGE_SUBHEADER_INITIAL_MESSAGE_ID,
  SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE,
  specificTableDataPropTypes,
  specificTableHeadersPropTypes,
  TABLES,
  textBlockPropTypes,
} from '../constants';
import { buildHelmet } from '../helpers/helmet';
import { SearchPageType } from '../types';
import { getMainSearchPageType } from '../utils';
import { BestCompanyIntl } from './BestCompany';
import EditableContent from './EditableContent';
import SearchView2 from './index2';
import { LinkSection } from './LinkSection';
import messages from './messages';
import ReducedSearchView from './ReducedSearchView';
import { ReturnTrip } from './ReturnTrip';
import { SpecificCompaniesTextIntl } from './SpecificCompaniesText';
import { SplitContentLayout } from './SplitCotentLayout';
import { CompaniesTable, TableSpecific } from './TableSpecific';

const Header = styled(H1)`
  padding: unset;
  margin: 0 0 17px 0;
`;

const SubHeader = styled(H2)`
  ${tw`m-0 p-0
  text-h3-m text-left
  sm:m-0 md:text-h3 sm:text-h3-m`};
`;

const Container = styled.div`
  ${tw`
    my-def
  `};
`;

const ContentWrapper = styled.div`
  ${tw`px-abs2 max-w-def xl:mx-auto`};
`;

const AddEditableContentButton = styled(ButtonSubmit)`
  ${tw`z-1`}
  position: fixed;
  bottom: 3rem;
  right: 3rem;
`;

const DestinationSectionsWrapper = styled.div`
  ${tw`flex flex-wrap justify-center`}
  gap:var(--size-medium2)
`;

const SearchView = ({
  addEmptyEditableContent,
  admin,
  companiesData,
  currentPageType,
  defaultDepartureDate,
  defaultReturnDate,
  deleteEditableContent,
  destinationCity,
  destinationCityCard,
  dynamicInformation,
  editableContent,
  generalTableData,
  generalTextData,
  isReduced,
  isTabletWidth,
  links,
  loading,
  metaImage,
  originCity,
  originCityCard,
  ratings,
  shortFaqs,
  specificTableData,
  specificTableHeaders,
  specificTextData,
  tripData,
}) => {
  if (loading) {
    return <LazyLoading />;
  }
  const mainPageType = getMainSearchPageType({
    destinationCity,
    originCity,
    tripData,
  });
  const pageTransportType = SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE[currentPageType];
  const isMainPageType =
    currentPageType === mainPageType || mainPageType === SearchPageType.GENERIC;

  const pageTitle = (
    <FormattedMessage
      {...messages.searchHeaderTitle}
      values={{
        origin: originCity.name,
        destination: destinationCity.name,

        transportType: (
          <FormattedMessage
            {...messages[pageTransportType ?? 'genericTransportType']}
          />
        ),
        extra: pageTransportType &&
          dynamicInformation.duration &&
          dynamicInformation.price && (
            <FormattedMessage
              {...messages.searchHeaderTitleSpecificExtra}
              values={{
                minPrice: getRoundPrice(dynamicInformation.price.min) + '€',
                minDuration: formatRoundedDuration(
                  dynamicInformation.duration.min,
                ),
              }}
            />
          ),
      }}
    />
  );

  const pageSubTitle = (
    <FormattedMessage
      {...(pageTransportType
        ? messages.searchHeaderSpecificSubTitle
        : messages.searchHeaderSubTitle)}
      values={{
        transportType: pageTransportType && (
          <FormattedMessage {...messages[pageTransportType]} />
        ),
        availableCompanies: dynamicInformation?.companies
          .map((company) => company.fullname)
          .join(', '),
      }}
    />
  );

  return (
    <div>
      <EditableSearchContent
        context={FileContext.SEARCH_HEADER}
        defaultArrivalCity={destinationCity}
        defaultDepartureCity={originCity}
        defaultOutboundDate={defaultDepartureDate}
        defaultReturnDate={defaultReturnDate}
        header={pageTitle}
        initialImageUrl={tripData.imageUrl}
        metadata={destinationCity.id}
        subHeader={pageSubTitle}
      />
      {buildHelmet(
        originCity,
        destinationCity,
        shortFaqs,
        tripData,
        ratings,
        metaImage,
        currentPageType,
        isMainPageType,
        dynamicInformation,
      )}

      {isReduced ? (
        <ReducedSearchView
          currentPageType={currentPageType}
          destinationCity={destinationCity}
          dynamicInformation={dynamicInformation}
          originCity={originCity}
          tripData={tripData}
        />
      ) : (
        <SearchView2
          currentPageType={currentPageType}
          destinationCity={destinationCity}
          dynamicInformation={dynamicInformation}
          isMainPageType={isMainPageType}
          legacyExtraContent={
            <LinkSection
              destinationCity={destinationCity}
              links={links}
              originCity={originCity}
            />
          }
          originCity={originCity}
          renderLagacyContent={(props) => {
            // eslint-disable-next-line react/prop-types
            const { afterCompaniesTableContent } = props;

            return (
              <React.Fragment>
                {specificTableHeaders && specificTableData && isMainPageType && (
                  <ContentWrapper>
                    <TableSpecific
                      data={specificTableData}
                      headers={specificTableHeaders}
                    />
                  </ContentWrapper>
                )}

                <CompaniesTable
                  companiesData={companiesData}
                  transportType={pageTransportType}
                />

                <BestCompanyIntl
                  companiesData={companiesData}
                  destinationCity={destinationCity}
                  originCity={originCity}
                  transportType={pageTransportType}
                />

                {specificTableHeaders && specificTableData && !isMainPageType && (
                  <ContentWrapper>
                    <TableSpecific
                      data={specificTableData}
                      headers={specificTableHeaders}
                    />
                  </ContentWrapper>
                )}

                {afterCompaniesTableContent}

                {isTabletWidth && (
                  <Container>
                    <Header id={SEARCH_PAGE_HEADER_INITIAL_MESSAGE_ID}>
                      {pageTitle}
                    </Header>
                    <SubHeader id={SEARCH_PAGE_SUBHEADER_INITIAL_MESSAGE_ID}>
                      {pageSubTitle}
                    </SubHeader>
                  </Container>
                )}
                {(generalTableData ||
                  specificTableHeaders ||
                  generalTextData ||
                  specificTextData ||
                  companiesData) && (
                  <SectionCol id={TABLES}>
                    {generalTextData && (
                      <SectionCol>
                        <Accordion
                          header={<CustomH3>{generalTextData.header}</CustomH3>}
                          id="generalText"
                          isOpenByDefault
                        >
                          <HTMLText>{generalTextData.content}</HTMLText>
                        </Accordion>
                      </SectionCol>
                    )}

                    {specificTextData && (
                      <SectionCol>
                        <Accordion
                          header={
                            <CustomH3>{specificTextData.header}</CustomH3>
                          }
                          id="specificText"
                          isOpenByDefault
                        >
                          <HTMLText>{specificTextData.content}</HTMLText>
                        </Accordion>
                      </SectionCol>
                    )}

                    <SpecificCompaniesTextIntl
                      companiesData={companiesData}
                      destinationCity={destinationCity}
                      originCity={originCity}
                      pageTransportType={pageTransportType}
                    />
                  </SectionCol>
                )}
                {shortFaqs.length > 0 && (
                  <SectionCol id={FAQ}>
                    <CustomH2>
                      <FormattedMessage
                        {...messages.faqsHeader}
                        values={{
                          origin: originCity.name,
                          destination: destinationCity.name,
                        }}
                      />
                    </CustomH2>

                    <SplitContentLayout>
                      {shortFaqs.map((faq) => (
                        <Accordion
                          key={faq.id}
                          header={<CustomH3>{faq.question}</CustomH3>}
                          id={faq.id}
                          isOpenByDefault
                        >
                          <HTMLText
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </Accordion>
                      ))}
                    </SplitContentLayout>
                  </SectionCol>
                )}
                {editableContent.length > 0 && (
                  <SectionCol>
                    {admin?.isAuthenticated ? (
                      <Fragment>
                        {editableContent.map((initialState) => (
                          <EditableContent
                            key={initialState.priority}
                            admin={admin}
                            deleteEditableContent={() =>
                              deleteEditableContent(initialState.priority)
                            }
                            initialState={initialState}
                            state={[
                              'search',
                              originCity.slug,
                              destinationCity.slug,
                              isMainPageType ? '' : pageTransportType,
                            ].join('-')}
                          />
                        ))}
                      </Fragment>
                    ) : (
                      <div
                        style={{
                          rowGap: 'var(--size-medium)',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {editableContent.map((faq) => (
                          <Accordion
                            key={faq.id}
                            header={<CustomH3>{faq.content.title}</CustomH3>}
                            isOpenByDefault
                          >
                            <HTMLText
                              dangerouslySetInnerHTML={{
                                __html: faq.content.body,
                              }}
                            />
                          </Accordion>
                        ))}
                      </div>
                    )}
                  </SectionCol>
                )}
                {admin?.isAuthenticated && (
                  <AddEditableContentButton onClick={addEmptyEditableContent}>
                    Add content
                  </AddEditableContentButton>
                )}
                {(originCityCard || destinationCityCard) && (
                  <SectionCol id={DESTINATIONS}>
                    <CustomH2>
                      <FormattedMessage {...messages.destinationCardsHeader} />
                    </CustomH2>
                    <DestinationSectionsWrapper>
                      {originCityCard && (
                        <DestinationSection
                          description={null}
                          destination={originCityCard.destinationCard}
                        />
                      )}
                      {destinationCityCard && (
                        <DestinationSection
                          description={null}
                          destination={destinationCityCard.destinationCard}
                        />
                      )}
                    </DestinationSectionsWrapper>
                  </SectionCol>
                )}
                <SectionCol>
                  <ReturnTrip
                    currentPageType={currentPageType}
                    destinationCity={destinationCity}
                    isMainPage={isMainPageType}
                    originCity={originCity}
                  />
                </SectionCol>
              </React.Fragment>
            );
          }}
          tripData={tripData}
        />
      )}
    </div>
  );
};

SearchView.propTypes = {
  addEmptyEditableContent: PropTypes.func,
  admin: PropTypes.object,
  companiesData: PropTypes.object,
  currentPageType: PropTypes.string,
  defaultDepartureDate: PropTypes.string,
  defaultReturnDate: PropTypes.string,
  deleteEditableContent: PropTypes.func,
  destinationCity: cityPropTypes,
  destinationCityCard: enhancedDestinationCard,
  dynamicInformation: PropTypes.object,
  editableContent: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.shape({
        body: PropTypes.string,
        title: PropTypes.string,
      }),
      id: PropTypes.number,
    }),
  ),
  generalTableData: generalTablePropTypes,
  generalTextData: textBlockPropTypes,
  isReduced: PropTypes.bool,
  isTabletWidth: PropTypes.bool,
  links: linksPropTypes,
  loading: PropTypes.bool,
  metaImage: PropTypes.string,
  originCity: cityPropTypes,
  originCityCard: enhancedDestinationCard,
  ratings: PropTypes.object, // api raw response
  searchHeaderBackground: PropTypes.string,
  shortFaqs: faqsPropTypes,
  specificTableData: specificTableDataPropTypes,
  specificTableHeaders: specificTableHeadersPropTypes,
  specificTextData: textBlockPropTypes,
  tripData: PropTypes.object, // api raw response
};

export { SearchView };
